import { useTranslation } from 'react-i18next';
import { Phone, Mail, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

const CONTACTS = [
  { name: '張明德', nameEn: 'Chang Ming-Te', role: '大會秘書長', roleEn: 'Secretary General', phone: '03-584-1001 #201', email: 'secretary@jianshi.gov.tw' },
  { name: '林秀美', nameEn: 'Lin Hsiu-Mei', role: '報名組組長', roleEn: 'Registration Director', phone: '03-584-1001 #202', email: 'registration@jianshi.gov.tw' },
  { name: '陳建宏', nameEn: 'Chen Chien-Hung', role: '競賽組組長', roleEn: 'Competition Director', phone: '03-584-1001 #203', email: 'competition@jianshi.gov.tw' },
  { name: '王美玲', nameEn: 'Wang Mei-Ling', role: '場地組組長', roleEn: 'Venue Director', phone: '03-584-1001 #204', email: 'venue@jianshi.gov.tw' },
  { name: '劉志偉', nameEn: 'Liu Chih-Wei', role: '媒體聯絡人', roleEn: 'Media Contact', phone: '03-584-1001 #205', email: 'media@jianshi.gov.tw' },
];

const FAQ = [
  {
    q: '如何報名參加運動會？',
    qEn: 'How to register for the games?',
    a: '各鄉區代表隊透過線上報名系統進行報名，由各鄉區承辦人統一操作。個人無法自行報名。',
    aEn: 'Township representatives register through the online system. Individual registration is not available.',
  },
  {
    q: '賽事期間有接駁車嗎？',
    qEn: 'Is there shuttle service during the event?',
    a: '大會提供高鐵新竹站至尖石鄉的免費接駁車，詳見交通資訊頁面。',
    aEn: 'Free shuttle service from HSR Hsinchu Station to Jianshi Township is available. See transportation page.',
  },
  {
    q: '比賽成績何時公布？',
    qEn: 'When will competition results be announced?',
    a: '比賽成績將即時更新於本網站「即時成績」頁面，各項決賽結束後即時公布。',
    aEn: 'Results are updated in real-time on the Live Scores page after each final.',
  },
  {
    q: '是否提供住宿安排？',
    qEn: 'Is accommodation arranged by the organizer?',
    a: '大會不統一安排住宿，但提供周邊住宿推薦名單，詳見住宿推薦頁面。',
    aEn: 'Accommodation is not arranged by the organizer. See our recommended accommodation list.',
  },
  {
    q: '觀眾可以入場觀賽嗎？',
    qEn: 'Can spectators attend the events?',
    a: '歡迎民眾自由入場觀賽，無需購票。部分場地有座位限制，建議提早入場。',
    aEn: 'Spectators are welcome. No tickets required. Some venues have limited seating.',
  },
];

export default function ContactsPage() {
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const isEn = i18n.language === 'en';

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="section-title">{t('contacts.title')}</h1>
      <p className="section-subtitle">{t('contacts.subtitle')}</p>

      {/* Office Hours */}
      <div className="card mb-8 flex items-center gap-3">
        <Clock className="w-5 h-5 text-primary shrink-0" />
        <div>
          <span className="font-medium">{t('contacts.officeHours')}:</span>
          <span className="text-text-secondary ml-2">
            {isEn ? 'Mon-Fri 08:30–17:30' : '週一至週五 08:30–17:30'}
          </span>
        </div>
      </div>

      {/* Contact Directory */}
      <div className="card mb-10">
        <h2 className="text-xl font-bold mb-6">{t('contacts.directory')}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b-2 border-primary/20">
                <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('contacts.name')}</th>
                <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('contacts.role')}</th>
                <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('contacts.phone')}</th>
                <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('contacts.email')}</th>
              </tr>
            </thead>
            <tbody>
              {CONTACTS.map((c, idx) => (
                <tr key={idx} className="border-b border-border/50 hover:bg-[var(--card-hover)]">
                  <td className="py-3 px-2 font-medium">{isEn ? c.nameEn : c.name}</td>
                  <td className="py-3 px-2 text-text-secondary">{isEn ? c.roleEn : c.role}</td>
                  <td className="py-3 px-2">
                    <a href={`tel:${c.phone}`} className="flex items-center gap-1 text-primary hover:underline text-sm">
                      <Phone className="w-3 h-3" /> {c.phone}
                    </a>
                  </td>
                  <td className="py-3 px-2">
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-primary hover:underline text-sm">
                      <Mail className="w-3 h-3" /> {c.email}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ */}
      <h2 className="text-xl font-bold mb-6">{t('contacts.faq')}</h2>
      <div className="space-y-3">
        {FAQ.map((item, idx) => (
          <div key={idx} className="card !p-0 overflow-hidden">
            <button
              onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--card-hover)] transition-colors"
            >
              <span className="font-medium pr-4">{isEn ? item.qEn : item.q}</span>
              {openFaq === idx ? (
                <ChevronUp className="w-5 h-5 shrink-0 text-primary" />
              ) : (
                <ChevronDown className="w-5 h-5 shrink-0 text-text-secondary" />
              )}
            </button>
            {openFaq === idx && (
              <div className="px-4 pb-4 text-text-secondary text-sm leading-relaxed border-t border-border/50 pt-3">
                {isEn ? item.aEn : item.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
